import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiArrowLeft, FiCheck, FiEdit, FiMapPin, FiPhone, FiMail, FiCalendar, FiUser, FiBriefcase, FiAward, FiTarget } from 'react-icons/fi';
import { getCategories, getCourses } from '../services/api';
import axios from 'axios';

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 2rem 0;
  
  @media (max-width: 768px) {
    padding: 1rem 0;
  }
`;

const FormWrapper = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  
  @media (max-width: 768px) {
    margin: 0 1rem;
    border-radius: 15px;
  }
`;

const Header = styled.div`
  background: linear-gradient(135deg, #01675f 0%, #028a6f 100%);
  color: white;
  padding: 2rem;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const HeaderTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  margin: 0 0 0.5rem 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  color: white;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeaderSubtitle = styled.p`
  font-size: 1.1rem;
  margin: 0;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ProgressContainer = styled.div`
  padding: 2rem;
  background: #f8f9fa;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const ProgressBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const ProgressStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  justify-content: center;
  position: relative;
  
  @media (max-width: 768px) {
    width: 100%;
    flex-direction: row;
  }
`;

const StepNumber = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  background: ${props => props.active ? '#01675f' : '#e9ecef'};
  color: ${props => props.active ? 'white' : '#6c757d'};
  transition: all 0.3s ease;
  box-shadow: ${props => props.active ? '0 3px 6px rgba(0, 0, 0, 0.1)' : 'none'};
  flex-shrink: 0; /* Prevent the circle from being squished */
  
  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
    font-size: 0.8rem;
  }
`;

const StepLabel = styled.span`
  font-weight: 500;
  color: ${props => props.active ? '#01675f' : '#6c757d'};
  text-align: center;
  width: 100%;
  max-width: 100px;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    max-width: none;
  }
`;

const ProgressLine = styled.div`
  flex: 1;
  height: 3px;
  background: ${props => props.active ? '#01675f' : '#e9ecef'};
  margin: 0 1rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const FormContent = styled.div`
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const StepTitle = styled.h2`
  font-size: 1.8rem;
  color: #01675f;
  margin: 0 0 1rem 0;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const StepSubtitle = styled.p`
  text-align: center;
  color: #6c757d;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.25rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const Required = styled.span`
  color: #dc3545;
  margin-left: 0.25rem;
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #01675f;
    box-shadow: 0 0 0 3px rgba(1, 103, 95, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 0.8rem;
    font-size: 0.9rem;
  }
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 10px;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #01675f;
    box-shadow: 0 0 0 3px rgba(1, 103, 95, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 0.8rem;
    font-size: 0.9rem;
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 10px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #01675f;
    box-shadow: 0 0 0 3px rgba(1, 103, 95, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 0.8rem;
    font-size: 0.9rem;
    min-height: 80px;
  }
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.25rem;
  margin-bottom: 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const CategoryCard = styled.div`
  border: 2px solid ${props => props.selected ? '#01675f' : '#e9ecef'};
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.selected ? 'rgba(1, 103, 95, 0.05)' : 'white'};
  
  &:hover {
    border-color: #01675f;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const CategoryIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: ${props => props.color || '#01675f'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
  }
`;

const CategoryInfo = styled.div`
  flex: 1;
`;

const CategoryName = styled.h3`
  margin: 0 0 0.25rem 0;
  color: #495057;
  font-size: 1.1rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CategoryDescription = styled.p`
  margin: 0;
  color: #6c757d;
  font-size: 0.9rem;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const CourseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.25rem;
  margin-bottom: 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const CourseCard = styled.div`
  border: 2px solid ${props => props.selected ? '#01675f' : '#e9ecef'};
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.selected ? 'rgba(1, 103, 95, 0.05)' : 'white'};
  
  &:hover {
    border-color: #01675f;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const CourseCode = styled.div`
  background: #01675f;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 700;
  display: inline-block;
  margin-bottom: 0.5rem;
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.2);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const CourseTitle = styled.h4`
  margin: 0 0 0.5rem 0;
  color: #495057;
  font-size: 1.1rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CourseLevel = styled.div`
  color: #6c757d;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const CourseDuration = styled.div`
  color: #6c757d;
  font-size: 0.9rem;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const Button = styled.button`
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  ${props => props.variant === 'primary' && `
    background: #01675f;
    color: white;
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.2);
    
    &:hover {
      background: #015a52;
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(1, 103, 95, 0.3);
    }
  `}
  
  ${props => props.variant === 'secondary' && `
    background: #6c757d;
    color: white;
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.2);
    
    &:hover {
      background: #5a6268;
      transform: translateY(-2px);
    }
  `}
  
  ${props => props.variant === 'outline' && `
    background: transparent;
    color: #01675f;
    border: 2px solid #01675f;
    
    &:hover {
      background: #01675f;
      color: white;
      text-shadow: 0 1px 1px rgba(0, 0, 0, 0.2);
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 1.5rem;
    font-size: 0.9rem;
  }
`;

const ReviewSection = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const ReviewTitle = styled.h3`
  color: #01675f;
  margin: 0 0 1.5rem 0;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const ReviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const ReviewItem = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  border-left: 4px solid #01675f;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ReviewLabel = styled.div`
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const ReviewValue = styled.div`
  color: #6c757d;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const EditButton = styled.button`
  background: none;
  border: none;
  color: #01675f;
  cursor: pointer;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.5rem;
  padding: 0;
  
  &:hover {
    text-decoration: underline;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin: 2rem 0;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 10px;
  
  @media (max-width: 768px) {
    padding: 1rem;
    margin: 1.5rem 0;
  }
`;

const Checkbox = styled.input`
  margin-top: 0.25rem;
`;

const CheckboxLabel = styled.label`
  color: #495057;
  line-height: 1.5;
  cursor: pointer;
  
  a {
    color: #01675f;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const SuccessContainer = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  background: #28a745;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem;
  color: white;
  font-size: 2rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  box-shadow: 0 6px 15px rgba(40, 167, 69, 0.3);
  
  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
`;

const SuccessTitle = styled.h2`
  color: #01675f;
  margin: 0 0 1rem 0;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const SuccessMessage = styled.p`
  color: #6c757d;
  font-size: 1.1rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  color: #01675f;
`;

const FreeQuotePage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    mobileNumber: '',
    preferredContact: '',
    state: '',
    selectedCategories: [],
    selectedCourses: [],
    experience: '',
    goals: '',
    additionalInfo: ''
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [categories, setCategories] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  // Fetch categories and courses on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [categoriesData, coursesData] = await Promise.all([
          getCategories(),
          getCourses({ limit: 1000 })
        ]);
        
        setCategories(categoriesData);
        setCourses(coursesData.courses || coursesData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCategoryToggle = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      selectedCategories: prev.selectedCategories.includes(categoryId)
        ? prev.selectedCategories.filter(id => id !== categoryId)
        : [...prev.selectedCategories, categoryId]
    }));
  };

  const handleCourseToggle = (courseId) => {
    setFormData(prev => ({
      ...prev,
      selectedCourses: prev.selectedCourses.includes(courseId)
        ? prev.selectedCourses.filter(id => id !== courseId)
        : [...prev.selectedCourses, courseId]
    }));
  };

  const nextStep = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      // Prepare data for API
      const submissionData = {
        ...formData,
        categories,
        courses
      };
      
      const response = await axios.post('/api/email/quote', submissionData);
      
      if (response.data.success) {
        setSubmitted(true);
      } else {
        alert('There was an error submitting your form. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your form. Please try again.');
    }
  };

  const renderProgressBar = () => (
    <ProgressContainer>
      <ProgressBar>
        <ProgressStep>
          <StepNumber active={currentStep >= 1}>1</StepNumber>
          <StepLabel active={currentStep >= 1}>Personal Info</StepLabel>
        </ProgressStep>
        <ProgressLine active={currentStep >= 2} />
        <ProgressStep>
          <StepNumber active={currentStep >= 2}>2</StepNumber>
          <StepLabel active={currentStep >= 2}>Categories</StepLabel>
        </ProgressStep>
        <ProgressLine active={currentStep >= 3} />
        <ProgressStep>
          <StepNumber active={currentStep >= 3}>3</StepNumber>
          <StepLabel active={currentStep >= 3}>Courses</StepLabel>
        </ProgressStep>
        <ProgressLine active={currentStep >= 4} />
        <ProgressStep>
          <StepNumber active={currentStep >= 4}>4</StepNumber>
          <StepLabel active={currentStep >= 4}>Contact & Location</StepLabel>
        </ProgressStep>
        <ProgressLine active={currentStep >= 5} />
        <ProgressStep>
          <StepNumber active={currentStep >= 5}>5</StepNumber>
          <StepLabel active={currentStep >= 5}>Review & Submit</StepLabel>
        </ProgressStep>
      </ProgressBar>
    </ProgressContainer>
  );

  const renderStep1 = () => (
    <>
      <StepTitle>Personal Information</StepTitle>
      <StepSubtitle>Tell us about yourself and your experience</StepSubtitle>
      
      <FormGrid>
        <FormGroup>
          <Label><FiUser /> First Name<Required>*</Required></Label>
          <Input
            type="text"
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            placeholder="Enter your first name"
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label><FiUser /> Last Name<Required>*</Required></Label>
          <Input
            type="text"
            value={formData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            placeholder="Enter your last name"
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label><FiMail /> Email Address<Required>*</Required></Label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="Enter your email address"
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label><FiPhone /> Phone Number<Required>*</Required></Label>
          <Input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            placeholder="Enter your phone number"
            required
          />
        </FormGroup>
      </FormGrid>
      
      <FormGroup>
        <Label><FiBriefcase /> Work Experience</Label>
        <TextArea
          value={formData.experience}
          onChange={(e) => handleInputChange('experience', e.target.value)}
          placeholder="Tell us about your work experience and background..."
        />
      </FormGroup>
      
      <FormGroup>
        <Label><FiTarget /> Career Goals</Label>
        <TextArea
          value={formData.goals}
          onChange={(e) => handleInputChange('goals', e.target.value)}
          placeholder="What are your career goals and how can RPL help you achieve them?"
        />
      </FormGroup>
    </>
  );

  const renderStep2 = () => (
    <>
      <StepTitle>Select Your Areas of Interest</StepTitle>
      <StepSubtitle>Choose the categories that match your experience and goals</StepSubtitle>
      
      {loading ? (
        <LoadingSpinner>Loading categories...</LoadingSpinner>
      ) : (
        <CategoryGrid>
          {categories.map((category) => (
            <CategoryCard
              key={category._id}
              selected={formData.selectedCategories.includes(category._id)}
              onClick={() => handleCategoryToggle(category._id)}
            >
              <CategoryHeader>
                <CategoryIcon color={category.color}>
                  {/* You can map icon names to actual icons here */}
                  <FiBriefcase />
                </CategoryIcon>
                <CategoryInfo>
                  <CategoryName>{category.name}</CategoryName>
                  <CategoryDescription>{category.description}</CategoryDescription>
                </CategoryInfo>
              </CategoryHeader>
            </CategoryCard>
          ))}
        </CategoryGrid>
      )}
    </>
  );

  const renderStep3 = () => (
    <>
      <StepTitle>Select Specific Courses</StepTitle>
      <StepSubtitle>Choose the specific qualifications you're interested in</StepSubtitle>
      
      {loading ? (
        <LoadingSpinner>Loading courses...</LoadingSpinner>
      ) : (
        <CourseGrid>
          {courses
            .filter(course => 
              // If no categories selected, show all courses
              formData.selectedCategories.length === 0 || 
              // Otherwise, filter courses by selected categories
              (course.category && formData.selectedCategories.includes(course.category._id || course.category))
            )
            .map((course) => (
              <CourseCard
                key={course._id}
                selected={formData.selectedCourses.includes(course._id)}
                onClick={() => handleCourseToggle(course._id)}
              >
                <CourseCode>{course.code}</CourseCode>
                <CourseTitle>{course.title}</CourseTitle>
                <CourseLevel>{course.level}</CourseLevel>
                <CourseDuration>Duration: {course.duration}</CourseDuration>
              </CourseCard>
            ))}
        </CourseGrid>
      )}
    </>
  );
  
  const renderStep4 = () => (
    <>
      <StepTitle>Contact & Location Details</StepTitle>
      <StepSubtitle>Help us provide you with the most relevant information</StepSubtitle>
      
      <FormGrid>
        <FormGroup>
          <Label><FiMapPin /> State/Territory<Required>*</Required></Label>
          <Select
            value={formData.state}
            onChange={(e) => handleInputChange('state', e.target.value)}
            required
          >
            <option value="">Select your state</option>
            <option value="NSW">New South Wales</option>
            <option value="VIC">Victoria</option>
            <option value="QLD">Queensland</option>
            <option value="WA">Western Australia</option>
            <option value="SA">South Australia</option>
            <option value="TAS">Tasmania</option>
            <option value="ACT">Australian Capital Territory</option>
            <option value="NT">Northern Territory</option>
          </Select>
        </FormGroup>
        
        <FormGroup>
          <Label><FiCalendar /> Preferred Contact Day/Time</Label>
          <Select
            value={formData.preferredContact}
            onChange={(e) => handleInputChange('preferredContact', e.target.value)}
          >
            <option value="">Select preferred time</option>
            <option value="Morning (8AM-12PM)">Morning (8AM-12PM)</option>
            <option value="Afternoon (12PM-5PM)">Afternoon (12PM-5PM)</option>
            <option value="Evening (5PM-8PM)">Evening (5PM-8PM)</option>
            <option value="Any time">Any time</option>
          </Select>
        </FormGroup>
      </FormGrid>
      
      <FormGroup>
        <Label>Additional Information</Label>
        <TextArea
          value={formData.additionalInfo}
          onChange={(e) => handleInputChange('additionalInfo', e.target.value)}
          placeholder="Any additional information you'd like us to know..."
        />
      </FormGroup>
    </>
  );

  const renderStep5 = () => (
    <>
      <StepTitle>Review Your Information</StepTitle>
      <StepSubtitle>Please review your details before submitting</StepSubtitle>
      
      <ReviewSection>
        <ReviewTitle>Personal Information</ReviewTitle>
        <ReviewGrid>
          <ReviewItem>
            <div>
              <ReviewLabel><FiUser /> Name</ReviewLabel>
              <ReviewValue>{formData.firstName} {formData.lastName}</ReviewValue>
            </div>
            <EditButton onClick={() => setCurrentStep(1)}>
              <FiEdit /> Edit
            </EditButton>
          </ReviewItem>
          
          <ReviewItem>
            <div>
              <ReviewLabel><FiMail /> Email</ReviewLabel>
              <ReviewValue>{formData.email || 'Not specified'}</ReviewValue>
            </div>
            <EditButton onClick={() => setCurrentStep(1)}>
              <FiEdit /> Edit
            </EditButton>
          </ReviewItem>
          
          <ReviewItem>
            <div>
              <ReviewLabel><FiPhone /> Phone</ReviewLabel>
              <ReviewValue>{formData.phone || 'Not specified'}</ReviewValue>
            </div>
            <EditButton onClick={() => setCurrentStep(1)}>
              <FiEdit /> Edit
            </EditButton>
          </ReviewItem>
          
          <ReviewItem>
            <div>
              <ReviewLabel><FiPhone /> Mobile</ReviewLabel>
              <ReviewValue>{formData.mobileNumber || 'Not specified'}</ReviewValue>
            </div>
            <EditButton onClick={() => setCurrentStep(1)}>
              <FiEdit /> Edit
            </EditButton>
          </ReviewItem>
          
          <ReviewItem>
            <div>
              <ReviewLabel><FiCalendar /> Preferred Contact</ReviewLabel>
              <ReviewValue>{formData.preferredContact || 'Not specified'}</ReviewValue>
            </div>
            <EditButton onClick={() => setCurrentStep(4)}>
              <FiEdit /> Edit
            </EditButton>
          </ReviewItem>
          
          <ReviewItem>
            <div>
              <ReviewLabel><FiMapPin /> State</ReviewLabel>
              <ReviewValue>{formData.state || 'Not specified'}</ReviewValue>
            </div>
            <EditButton onClick={() => setCurrentStep(4)}>
              <FiEdit /> Edit
            </EditButton>
          </ReviewItem>
        </ReviewGrid>
      </ReviewSection>
      
      <ReviewSection>
        <ReviewTitle>Selected Categories & Courses</ReviewTitle>
        <ReviewGrid>
          <ReviewItem>
            <div>
              <ReviewLabel><FiBriefcase /> Categories</ReviewLabel>
              <ReviewValue>
                {formData.selectedCategories.length > 0 
                  ? categories
                      .filter(cat => formData.selectedCategories.includes(cat._id))
                      .map(cat => cat.name)
                      .join(', ')
                  : 'No categories selected'
                }
              </ReviewValue>
            </div>
            <EditButton onClick={() => setCurrentStep(2)}>
              <FiEdit /> Edit
            </EditButton>
          </ReviewItem>
          
          <ReviewItem>
            <div>
              <ReviewLabel><FiAward /> Courses</ReviewLabel>
              <ReviewValue>
                {formData.selectedCourses.length > 0 
                  ? courses
                      .filter(course => formData.selectedCourses.includes(course._id))
                      .map(course => `${course.code} - ${course.title}`)
                      .join(', ')
                  : 'No courses selected'
                }
              </ReviewValue>
            </div>
            <EditButton onClick={() => setCurrentStep(3)}>
              <FiEdit /> Edit
            </EditButton>
          </ReviewItem>
        </ReviewGrid>
      </ReviewSection>
      
      <CheckboxContainer>
        <Checkbox
          type="checkbox"
          id="terms"
          checked={agreedToTerms}
          onChange={(e) => setAgreedToTerms(e.target.checked)}
        />
        <CheckboxLabel htmlFor="terms">
          * I have read and agree to be bound by the <a href="/terms-conditions">terms and conditions</a> of RPL Support
        </CheckboxLabel>
      </CheckboxContainer>
    </>
  );

  const renderSuccessStep = () => (
    <SuccessContainer>
      <SuccessIcon>
        <FiCheck />
      </SuccessIcon>
      <SuccessTitle>Thank You!</SuccessTitle>
      <SuccessMessage>
        Your quote request has been submitted successfully. We'll review your information 
        and get back to you within 24 hours with your personalized quote.
      </SuccessMessage>
      <Button as={Link} to="/" variant="primary">
        Return to Home
      </Button>
    </SuccessContainer>
  );

  if (submitted) {
    return (
      <Container>
        <FormWrapper>
          <Header>
            <HeaderTitle>Free Quote Request</HeaderTitle>
            <HeaderSubtitle>Get your personalized RPL assessment quote</HeaderSubtitle>
          </Header>
          {renderSuccessStep()}
        </FormWrapper>
      </Container>
    );
  }

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      case 5:
        return renderStep5();
      default:
        return renderStep1();
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.firstName && formData.lastName && formData.email && formData.phone;
      case 2:
        return formData.selectedCategories.length > 0;
      case 3:
        return formData.selectedCourses.length > 0;
      case 4:
        return formData.state;
      case 5:
        return agreedToTerms;
      default:
        return true;
    }
  };

  return (
    <Container>
      <FormWrapper>
        <Header>
          <HeaderTitle>Free Quote Request</HeaderTitle>
          <HeaderSubtitle>Get your personalized RPL assessment quote</HeaderSubtitle>
        </Header>
        
        {renderProgressBar()}
        
        <FormContent>
          {renderCurrentStep()}
          
          <ButtonGroup>
            {currentStep > 1 && (
              <Button type="button" variant="outline" onClick={prevStep}>
                <FiArrowLeft /> Previous
              </Button>
            )}
            
            {currentStep < 5 && (
              <Button 
                type="button" 
                variant="primary" 
                onClick={nextStep}
                disabled={!canProceed()}
              >
                Next <FiArrowRight />
              </Button>
            )}
            
            {currentStep === 5 && (
              <Button 
                type="button" 
                variant="primary" 
                onClick={nextStep}
                disabled={!canProceed()}
              >
                Submit Request <FiCheck />
              </Button>
            )}
          </ButtonGroup>
        </FormContent>
      </FormWrapper>
    </Container>
  );
};

export default FreeQuotePage; 